import type { TranscribeOutput } from '@travel-planner/shared/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAgentPanelStore } from './use-agent-panel-store';

const MIN_RECORDING_MS = 250;
const MAX_DURATION_MS = 30_000;

const SUPPORTED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
];

export type AgentVoicePhase =
  | 'idle'
  | 'requesting-permission'
  | 'recording'
  | 'transcribing'
  | 'error';

export type AgentVoiceErrorReason =
  | 'mic-denied'
  | 'mic-unsupported'
  | 'too-long'
  | 'too-short'
  | 'too-large'
  | 'unavailable'
  | 'billing'
  | 'auth'
  | 'rate-limited'
  | 'aborted'
  | 'unknown';

// Server-side failure reasons forwarded as the tRPC error `message`. The
// catch in `recorder.onstop` maps an unknown error back to a reason via
// this set — anything else falls through to 'unavailable'.
const SERVER_ERROR_REASONS = new Set<string>([
  'too-long',
  'too-large',
  'unavailable',
  'billing',
  'auth',
  'rate-limited',
]);

function isServerErrorReason(msg: string): msg is AgentVoiceErrorReason {
  return SERVER_ERROR_REASONS.has(msg);
}

function reasonFromError(
  err: unknown,
  durationMs: number
): AgentVoiceErrorReason {
  if (err instanceof Error && isServerErrorReason(err.message)) {
    return err.message;
  }
  // Fallback heuristic: if the recorder ran for the full window the most
  // likely cause is duration, even when we couldn't read the server msg.
  if (durationMs >= MAX_DURATION_MS) return 'too-long';
  return 'unavailable';
}

// Shared error-copy helper. The agent search bar surfaces this in the
// drawer footer; the email step renders it inline above the textarea so
// dictation failures aren't silent in Generator mode (where the search
// bar is hidden).
export function voiceErrorMessage(reason: AgentVoiceErrorReason): string {
  switch (reason) {
    case 'too-short':
      // Used both for "audio < 250 ms" and for "API returned empty
      // transcript" — both look the same to the consultant.
      return "Couldn't catch that — try speaking a bit longer.";
    case 'too-long':
      return 'Recording too long — keep it under 30 seconds.';
    case 'too-large':
      return 'Recording is too large — try a shorter clip.';
    case 'mic-denied':
      return 'Microphone access denied — check your browser settings.';
    case 'mic-unsupported':
      return "This browser can't record audio — try a different one.";
    case 'billing':
      return 'Transcription credits exhausted — please contact your administrator.';
    case 'auth':
      return 'Transcription service is misconfigured — please contact your administrator.';
    case 'rate-limited':
      return 'Too many transcription requests — wait a moment and try again.';
    case 'unavailable':
      return 'Transcription is unavailable right now — try typing instead.';
    default:
      // 'aborted', 'unknown', and any future reason fall through here.
      return 'Voice transcription failed — try again.';
  }
}

export type TranscribeMutate = (input: {
  audioBase64: string;
  mimeType: string;
  durationMs: number;
}) => Promise<TranscribeOutput>;

export type UseAgentVoiceArgs = {
  onTranscribed: (text: string) => void;
  transcribeMutate: TranscribeMutate;
  mediaDevices?: MediaDevices;
  audioContextCtor?: typeof AudioContext | null;
};

export type UseAgentVoiceReturn = {
  phase: AgentVoicePhase;
  errorReason: AgentVoiceErrorReason | null;
  audioLevel: number;
  elapsedMs: number;
  isMicDenied: boolean;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  forceStop: () => void;
  /** Clears any sticky `errorReason`. Used when the consultant types to
   * dismiss a stale voice-error banner. Idempotent. */
  clearError: () => void;
};

function pickMimeType(): string {
  for (const mime of SUPPORTED_MIMES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return SUPPORTED_MIMES[0];
}

export function useAgentVoice({
  onTranscribed,
  transcribeMutate,
  mediaDevices = typeof navigator !== 'undefined'
    ? navigator.mediaDevices
    : undefined,
  audioContextCtor = typeof AudioContext !== 'undefined'
    ? AudioContext
    : undefined,
}: UseAgentVoiceArgs): UseAgentVoiceReturn {
  const [phase, setPhase] = useState<AgentVoicePhase>('idle');
  const [errorReason, setErrorReason] = useState<AgentVoiceErrorReason | null>(
    null
  );
  const [audioLevel, setAudioLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const setMicDenied = useAgentPanelStore((s) => s.setMicDenied);
  const micDenied = useAgentPanelStore((s) => s.micDenied);

  const phaseRef = useRef<AgentVoicePhase>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number>(0);
  const abortRef = useRef<AbortController | null>(null);
  const startTsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const setPhaseTracked = useCallback((p: AgentVoicePhase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    if (recorderRef.current) {
      try {
        recorderRef.current.stop();
      } catch {
        // already stopped
      }
      recorderRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch {
        // already closed
      }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
    setElapsedMs(0);
  }, []);

  // Unmount cleanup — release streams, contexts, timers
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
      cleanup();
    };
  }, [cleanup]);

  const stopRecorder = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async () => {
    // Allow restart from `'error'` so a failed transcription doesn't lock
    // the mic. Any other in-flight phase (recording, transcribing,
    // requesting-permission) is left alone.
    if (phaseRef.current !== 'idle' && phaseRef.current !== 'error') return;

    if (micDenied) {
      setErrorReason('mic-denied');
      setPhaseTracked('error');
      return;
    }

    if (!mediaDevices?.getUserMedia) {
      setErrorReason('mic-unsupported');
      setPhaseTracked('error');
      return;
    }

    cancelledRef.current = false;
    setPhaseTracked('requesting-permission');
    setErrorReason(null);

    let stream: MediaStream;
    try {
      stream = await mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const domErr = err as DOMException;
      if (
        domErr?.name === 'NotAllowedError' ||
        domErr?.name === 'PermissionDeniedError'
      ) {
        setMicDenied(true);
        setErrorReason('mic-denied');
      } else {
        setErrorReason('unknown');
      }
      setPhaseTracked('error');
      return;
    }

    if (cancelledRef.current) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }

    streamRef.current = stream;

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    const mimeType = pickMimeType();

    const ctx = audioContextCtor ? new audioContextCtor() : null;
    audioCtxRef.current = ctx;

    if (ctx) {
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        setAudioLevel(Math.sqrt(sum / dataArray.length));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    startTsRef.current = Date.now();
    setElapsedMs(0);
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTsRef.current);
    }, 100);

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = async () => {
      cleanup();

      if (cancelledRef.current) return;

      const durationMs = Date.now() - startTsRef.current;

      if (durationMs < MIN_RECORDING_MS) {
        setPhaseTracked('idle');
        setErrorReason('too-short');
        return;
      }

      const blob = new Blob(chunks, { type: mimeType });
      const buffer = await blob.arrayBuffer();

      const bytes = new Uint8Array(buffer);
      const b64Chunks: string[] = [];
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        b64Chunks.push(String.fromCharCode(...chunk));
      }
      const audioBase64 = btoa(b64Chunks.join(''));

      setPhaseTracked('transcribing');

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = await transcribeMutate({
          audioBase64,
          mimeType,
          durationMs,
        });

        if (controller.signal.aborted) return;

        if (!result.transcript) {
          setPhaseTracked('idle');
          setErrorReason('too-short');
          return;
        }

        onTranscribed(result.transcript);
        setPhaseTracked('idle');
        setErrorReason(null);
      } catch (err) {
        if (controller.signal.aborted) return;

        setErrorReason(reasonFromError(err, durationMs));
        setPhaseTracked('error');
      } finally {
        abortRef.current = null;
      }
    };

    recorder.start(250);
    setPhaseTracked('recording');

    // Auto-stop after max duration — call stopRecorder directly
    // to avoid stale closure over phase state
    autoStopRef.current = setTimeout(() => {
      if (recorderRef.current?.state === 'recording' && !cancelledRef.current) {
        recorderRef.current.stop();
      }
    }, MAX_DURATION_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    micDenied,
    mediaDevices,
    audioContextCtor,
    setMicDenied,
    cleanup,
    setPhaseTracked,
    transcribeMutate,
    onTranscribed,
  ]);

  const stop = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    stopRecorder();
  }, [stopRecorder]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    cleanup();
    setPhaseTracked('idle');
    setErrorReason(null);
  }, [cleanup, setPhaseTracked]);

  const forceStop = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    stopRecorder();
  }, [stopRecorder]);

  const clearError = useCallback(() => {
    if (phaseRef.current === 'error') setPhaseTracked('idle');
    setErrorReason(null);
  }, [setPhaseTracked]);

  return {
    phase,
    errorReason,
    audioLevel,
    elapsedMs,
    isMicDenied: micDenied,
    start,
    stop,
    cancel,
    forceStop,
    clearError,
  };
}
